import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Text, Flex, HStack, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton,
  DrawerHeader, DrawerBody, DrawerFooter, Button, Icon, Badge, SimpleGrid, Spinner,
} from '@chakra-ui/react';
import { FiTrendingDown, FiTrendingUp, FiActivity, FiCheckCircle } from 'react-icons/fi';
import client from '../utils/fetchClient';
import StatusBadge from './ui/StatusBadge';

const DOT = { passed: '#14b36e', failed: '#d03b3b', skipped: '#9CA3AF', flaky: '#b7791f' };

const fmtMs = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms || 0)}ms`);
const fmtAgo = (iso) => {
  if (!iso) return '-';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

/** Derive a regression / improvement verdict from the recent trend. */
function verdictOf(trend, currentStatus) {
  if (!trend || trend.length < 4) return null;
  const recent = trend.slice(-12);
  const older = trend.slice(-24, -12);
  const rate = (arr) => (arr.length ? arr.filter((p) => p.status === 'passed').length / arr.length : 0);
  const flakeRate = (arr) => (arr.length ? arr.filter((p) => p.status === 'flaky').length / arr.length : 0);
  const r = rate(recent), o = rate(older);

  if (currentStatus === 'failed' && o >= 0.8 && r < 0.8) {
    const lastPass = [...trend].reverse().find((p) => p.status === 'passed');
    return {
      kind: 'Regressed',
      icon: FiTrendingDown,
      fg: '#92600b', bg: '#fef3c7', border: '#fde68a',
      text: `Passed most of the previous runs${lastPass ? `; last green ${fmtAgo(lastPass.date)}` : ''}. Started failing recently.`,
    };
  }
  if (r > o + 0.15) {
    return {
      kind: 'Improved',
      icon: FiTrendingUp,
      fg: '#0e6b43', bg: '#e6f9f0', border: '#b8eed6',
      text: `Pass rate up from ${Math.round(o * 100)}% to ${Math.round(r * 100)}% over the last runs.`,
    };
  }
  if (flakeRate(recent) > 0.15) {
    return {
      kind: 'Flaky',
      icon: FiActivity,
      fg: '#92600b', bg: '#fef3c7', border: '#fde68a',
      text: `${Math.round(flakeRate(recent) * 100)}% of recent runs needed a retry.`,
    };
  }
  return {
    kind: 'Stable',
    icon: FiCheckCircle,
    fg: '#0e6b43', bg: '#e6f9f0', border: '#b8eed6',
    text: `Consistent — ${Math.round(r * 100)}% pass rate across recent runs.`,
  };
}

export default function TestCaseDrawer({ open, onClose, testCase, testcase }) {
  const passed = testCase || testcase || null;
  const passedId = passed?.id || null;
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiState, setAiState] = useState('idle'); // idle | loading | ready | error
  const [aiError, setAiError] = useState('');

  // The list rows only carry a thin record; pull the full case (error stack,
  // line, artefacts) and its history when the drawer opens.
  useEffect(() => {
    if (!open || !passedId) return;
    setDetail(null);
    setHistory(null);
    setHistLoading(true);
    client.get(`/api/test-cases/${passedId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setDetail(data))
      .catch(() => setDetail(null));
    client.get(`/api/test-cases/${passedId}/history`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setHistory(data))
      .catch(() => setHistory(null))
      .finally(() => setHistLoading(false));
  }, [open, passedId]);

  const tc = useMemo(() => ({ ...(passed || {}), ...(detail || {}) }), [passed, detail]);

  // reset AI state when the drawer target changes
  useEffect(() => { setAi(null); setAiState('idle'); setAiError(''); }, [passedId]);

  const runAi = () => {
    setAiState('loading');
    setAiError('');
    client.post('/api/ai-summary', {
      body: {
        title: tc?.title,
        suite: tc?.suite,
        file: tc?.file,
        line: tc?.line,
        errorStack: tc?.errorStack,
        errorMessage: tc?.errorMessage,
        pageSnapshot: tc?.pageSnapshot,
      },
    })
      .then(async (r) => {
        if (!r.ok) {
          const raw = await r.text().catch(() => '');
          let msg = `HTTP ${r.status}`;
          try { const j = JSON.parse(raw); msg = j.message || j.error || msg; } catch { if (raw) msg = raw.slice(0, 120); }
          throw new Error(msg);
        }
        return r.json();
      })
      .then((d) => { setAi(d.summary || ''); setAiState('ready'); })
      .catch((e) => { setAiError(e.message || 'AI analysis unavailable'); setAiState('error'); });
  };

  const trend = useMemo(() => history?.trendData || [], [history]);
  const metrics = history?.metrics || null;
  const verdict = useMemo(() => verdictOf(trend, tc?.status), [trend, tc?.status]);

  const durPoly = useMemo(() => {
    const pts = trend.slice(-40);
    if (pts.length < 2) return '';
    const W = 400, H = 40;
    const ds = pts.map((p) => p.duration || 0);
    const mn = Math.min(...ds), mx = Math.max(...ds);
    return pts.map((p, i) => {
      const x = ((i * W) / (pts.length - 1)).toFixed(1);
      const y = (4 + (1 - ((p.duration || 0) - mn) / (mx - mn || 1)) * (H - 8)).toFixed(1);
      return `${x},${y}`;
    }).join(' ');
  }, [trend]);

  const isFailed = tc?.status === 'failed';

  return (
    <Drawer isOpen={open} onClose={onClose} placement="right" size={{ base: 'full', md: 'md' }}>
      <DrawerOverlay />
      <DrawerContent maxW={{ base: '100vw', md: '480px' }}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" px={5} py={4}>
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="gray.400" mb={2}>
            Test case
          </Text>
          <HStack align="flex-start" spacing={2.5}>
            <Box w="9px" h="9px" borderRadius="full" bg={DOT[tc?.status] || '#9CA3AF'} mt="6px" flexShrink={0} />
            <Box minW={0} flex="1">
              <Text fontSize="15px" fontWeight="700" lineHeight="1.35" color="gray.900">{tc?.title || 'Test case'}</Text>
              <Text fontSize="11.5px" color="gray.400" fontFamily="mono" mt="2px" overflowWrap="anywhere" title={[tc?.suite, tc?.file].filter(Boolean).join(' · ')}>
                {[tc?.suite, tc?.file].filter(Boolean).join(' · ')}{tc?.line ? `:${tc.line}` : ''}
              </Text>
            </Box>
          </HStack>
          <Box mt={2}><StatusBadge status={tc?.status} /></Box>
        </DrawerHeader>

        <DrawerBody px={5} py={4}>
          {verdict && (
            <Flex gap={2.5} align="flex-start" bg={verdict.bg} border="1px solid" borderColor={verdict.border} borderRadius="12px" p={3} mb={4}>
              <Icon as={verdict.icon} color={verdict.fg} boxSize={4} mt="1px" flexShrink={0} />
              <Box>
                <Text fontSize="12.5px" fontWeight="700" color={verdict.fg}>{verdict.kind}</Text>
                <Text fontSize="12px" color="gray.600" lineHeight="1.5" mt="2px">{verdict.text}</Text>
              </Box>
            </Flex>
          )}

          {histLoading && <Spinner size="sm" color="brand.500" mb={4} />}

          {metrics && (
            <SimpleGrid columns={2} spacing={2.5} mb={5}>
              <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
                <Text fontSize="11px" color="gray.500">Pass rate</Text>
                <Text fontSize="20px" fontWeight="800" color="gray.900">{Math.round((metrics.passRate || 0) * 100)}%</Text>
              </Box>
              <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
                <Text fontSize="11px" color="gray.500">Avg duration</Text>
                <Text fontSize="20px" fontWeight="800" color="gray.900">{fmtMs(metrics.avgDuration)}</Text>
              </Box>
              <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
                <Text fontSize="11px" color="gray.500">Flakiness</Text>
                <Text fontSize="20px" fontWeight="800" color="gray.900">{Math.round((metrics.flakinessScore || 0) * 100)}%</Text>
              </Box>
              <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
                <Text fontSize="11px" color="gray.500">Last passed</Text>
                <Text fontSize="20px" fontWeight="800" color="gray.900">{fmtAgo(metrics.lastPassed)}</Text>
              </Box>
            </SimpleGrid>
          )}

          {trend.length > 0 && (
            <Box mb={5}>
              <Text fontSize="12px" fontWeight="700" color="gray.900" mb={2}>History · last {Math.min(trend.length, 40)} runs</Text>
              <Flex gap="3px" wrap="wrap" mb={2}>
                {trend.slice(-40).map((p, i) => (
                  <Box key={i} w="11px" h="18px" borderRadius="3px" bg={DOT[p.status] || '#9CA3AF'} title={new Date(p.date).toLocaleString()} />
                ))}
              </Flex>
              {durPoly && (
                <Box as="svg" viewBox="0 0 400 44" preserveAspectRatio="none" w="100%" h="38px" display="block">
                  <polyline points={durPoly} fill="none" stroke="#9CA3AF" strokeWidth="1.5" />
                </Box>
              )}
            </Box>
          )}

          {isFailed && (
            <Box mb={5}>
              <Text fontSize="12px" fontWeight="700" color="gray.900" mb={2}>Error</Text>
              {tc?.errorMessage && (
                <Text fontSize="12px" color="status.fail" fontWeight="600" mb={2}>{tc.errorMessage}</Text>
              )}
              {tc?.errorStack && (
                <Box as="pre" fontSize="11px" lineHeight="1.65" color="gray.700" fontFamily="mono" whiteSpace="pre-wrap" bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="10px" p={3} overflowX="auto">
                  {tc.errorStack}
                </Box>
              )}
            </Box>
          )}

          {isFailed && tc?.pageSnapshot && (
            <Box mb={5}>
              <Text fontSize="12px" fontWeight="700" color="gray.900" mb={2}>Page content at failure</Text>
              <Text fontSize="11px" color="gray.500" mb={2}>What was actually rendered when this test failed - the evidence the AI analysis below compares against the expected value.</Text>
              <Box as="pre" fontSize="11px" lineHeight="1.65" color="gray.700" fontFamily="mono" whiteSpace="pre-wrap" bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="10px" p={3} maxH="160px" overflowY="auto">
                {tc.pageSnapshot}
              </Box>
            </Box>
          )}

          {isFailed && (
            <Box border="1px solid" borderColor="brand.100" bg="brand.50" borderRadius="14px" p={4} mb={5}>
              <HStack spacing={2} mb={2.5}>
                <Icon as={FiActivity} color="brand.600" boxSize={4} />
                <Text fontSize="12.5px" fontWeight="700" color="brand.700">AI failure analysis</Text>
              </HStack>
              {aiState === 'idle' && (
                <>
                  <Text fontSize="12px" color="gray.600" mb={3}>Get a likely root cause and a debug checklist for this failure.</Text>
                  <Button size="sm" onClick={runAi}>Analyse this failure</Button>
                </>
              )}
              {aiState === 'loading' && <HStack><Spinner size="sm" color="brand.500" /><Text fontSize="12px" color="gray.600">Analysing…</Text></HStack>}
              {aiState === 'ready' && (
                <>
                  <Text as="pre" fontSize="12px" color="gray.700" fontFamily="inherit" whiteSpace="pre-wrap" lineHeight="1.6" mb={3}>{ai}</Text>
                  <Button size="sm" variant="outline" onClick={runAi}>Regenerate</Button>
                </>
              )}
              {aiState === 'error' && (
                <>
                  <Text fontSize="12px" color="gray.500" mb={2}>AI analysis unavailable ({aiError}).</Text>
                  <Button size="sm" variant="outline" onClick={runAi}>Retry</Button>
                </>
              )}
            </Box>
          )}

          {['screenshot', 'video', 'trace'].some((k) => tc?.[k]) && (
            <HStack spacing={2} wrap="wrap">
              {['screenshot', 'video', 'trace'].filter((k) => tc?.[k]).map((k) => (
                <Badge key={k} textTransform="capitalize" bg="white" border="1px solid" borderColor="gray.200" color="gray.700" fontWeight="600" borderRadius="full" px={2.5} py={1}>
                  {k}
                </Badge>
              ))}
            </HStack>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" px={5} py={3} gap={2}>
          {tc?.testRunId && (
            <Button as="a" href={`/test-runs/${tc.testRunId}`} size="sm" variant="outline">View run</Button>
          )}
          <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
